import { t } from '../i18n';
import { useState, useEffect, useCallback } from 'react';
import { AppState } from 'react-native';
import { doc, limit, query, updateDoc, serverTimestamp, writeBatch, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { firestoreCache, CACHE_KEYS, CACHE_TTL } from '../utils/firestoreCache';
import { guardedGetDoc, guardedGetDocs } from '../utils/guardedFirestore';
import { getOfferingKey, offeringDocument } from '../utils/courseIdentity';
import {
  isCanonicalReviewForPeriod,
  isValidReviewIndex,
  reviewIndexCollection,
  reviewIndexDocument,
} from '../utils/reviewIndex';
import { decideReviewGate } from '../utils/reviewGateDecision';
import { collectValidIndexKeys, selectRepairCourses } from '../utils/reviewGateResolution';
import {
  isInReviewPeriod,
  getCurrentSemester,
  FORCE_REVIEW_GATE,
} from '../constants/reviewPeriods';
import { GateStatus } from '../types/gate';

const REQUIRED_REVIEW_COUNT = 2;

interface UseReviewGateResult {
  isGated: boolean;
  reviewCount: number;
  requiredCount: number;
  loading: boolean;
  status: GateStatus;
  error: string | null;
  retry: () => Promise<void>;
  setExempt: () => Promise<void>;
  notifyReviewSubmitted: () => void;
  currentSemester: { academicYear: string; semester: string };
}

/**
 * レビューゲートフック
 * ユーザーが講義レビューを必要数投稿していない場合、ゲート状態を返す
 */
export const useReviewGate = (): UseReviewGateResult => {
  const [isGated, setIsGated] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<GateStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [currentSemester, setCurrentSemester] = useState({ academicYear: '', semester: '' });

  const markUnavailable = useCallback((cause: unknown) => {
    console.error('[useReviewGate] Failed to check gate status:', cause);
    setIsGated(true);
    setError(t("レビュー投稿状況を確認できませんでした"));
    setStatus('unavailable');
  }, []);

  const checkGateStatus = useCallback(async (userUid: string, ignoreCache = false) => {
      setLoading(true);
      setError(null);

      // 1. 評価期間外ならゲートなし
      if (!isInReviewPeriod()) {
        setIsGated(false);
        setStatus('ready');
        setLoading(false);
        return;
      }

      const semester = getCurrentSemester();
      setCurrentSemester(semester);
      // 期間中は確認が完了するまで制限対象画面を開放しない。
      setIsGated(true);
      setStatus('loading');

      // --- キャッシュヒット確認 ---
      const cacheKey = CACHE_KEYS.reviewGate(userUid, semester.academicYear, semester.semester);
      const cached = ignoreCache
        ? null
        : firestoreCache.get<{ isGated: boolean; reviewCount: number }>(cacheKey);
      if (cached) {
        setIsGated(cached.isGated);
        setReviewCount(cached.reviewCount);
        setStatus('ready');
        setLoading(false);
        return;
      }

      try {
        // FORCE_REVIEW_GATE 有効時は免除フラグ確認をスキップ
        // 新規ユーザーであってもアカウント作成時期に関わらず同じ要件を適用するため、
        // 新規ユーザー自動免除の判定は行わない（明示的な reviewExempt のみ免除対象）
        if (!FORCE_REVIEW_GATE) {
          // 2. 免除フラグ確認
          const userDoc = await guardedGetDoc(doc(db, 'users', userUid));
          if (!userDoc) {
            throw new Error('review gate user read skipped by quota guard or circuit breaker');
          }
          const userData = userDoc.exists() ? userDoc.data() : null;
          const exemptData = userData?.reviewExempt;

          if (exemptData) {
            const exemptSemester = exemptData.semester;
            const currentSemesterKey = `${semester.academicYear}${semester.semester}`;
            if (exemptSemester === currentSemesterKey) {
              // 今学期の免除フラグあり → 免除
              setIsGated(false);
              setStatus('ready');
              return;
            }
          }
        }

        // 3. 本人専用索引を先に確認する。レビュー事実の読取りはゲート解除に必要な最大2件に制限する。
        const indexSnapshot = await guardedGetDocs(query(
          reviewIndexCollection(db, userUid),
          where('academicYear', '==', semester.academicYear),
          where('semester', '==', semester.semester),
          limit(REQUIRED_REVIEW_COUNT)
        ));
        if (!indexSnapshot) {
          throw new Error('review gate index read skipped by quota guard or circuit breaker');
        }

        const { indexCount, indexedOfferingKeys } = collectValidIndexKeys(
          indexSnapshot.docs,
          userUid,
          semester.academicYear,
          semester.semester,
          isValidReviewIndex
        );

        if (indexCount >= REQUIRED_REVIEW_COUNT) {
          const decision = decideReviewGate(indexCount, REQUIRED_REVIEW_COUNT);
          setReviewCount(decision.reviewCount);
          setIsGated(decision.isGated);
          setStatus('ready');
          firestoreCache.set(cacheKey, decision, CACHE_TTL.REVIEW_GATE);
          return;
        }

        // 4. 索引が必要数未満の場合のみ、既知の時間割授業の正規レビューを照合して不足分を自己修復する。
        const scheduleDoc = await guardedGetDoc(doc(db, 'userSchedules', `${userUid}_main`));
        if (!scheduleDoc) {
          throw new Error('review gate schedule read skipped by quota guard or circuit breaker');
        }
        const courses = scheduleDoc.exists() ? scheduleDoc.data()?.courses || [] : [];
        const enrolledCourses = Array.from(
          new Set(
            courses
              .filter((course: any) => !course.isCustom && course.id && course.academicYear)
              .map((course: any) => JSON.stringify({ id: course.id, academicYear: course.academicYear }))
          )
        ).map((course: string) => JSON.parse(course));

        const repairCandidates: Array<{ course: any; data: Record<string, any>; reviewPath: string }> = [];
        const repairCourses = selectRepairCourses(
          enrolledCourses,
          indexedOfferingKeys,
          getOfferingKey
        );
        for (const { course } of repairCourses) {
          if (indexCount + repairCandidates.length >= REQUIRED_REVIEW_COUNT) break;
          const reviewRef = doc(offeringDocument(db, course), 'reviews', userUid);
          const reviewDoc = await guardedGetDoc(reviewRef);
          if (!reviewDoc) {
            throw new Error('review gate review read skipped by quota guard or circuit breaker');
          }
          if (!reviewDoc.exists()) continue;

          const data = reviewDoc.data() as Record<string, any>;
          if (isCanonicalReviewForPeriod(data, semester.academicYear, semester.semester)) {
            repairCandidates.push({ course, data, reviewPath: reviewRef.path });
          }
        }

        if (repairCandidates.length > 0) {
          const repairBatch = writeBatch(db);
          repairCandidates.forEach(({ course, data, reviewPath }) => {
            repairBatch.set(reviewIndexDocument(db, userUid, course), {
              courseId: course.id,
              academicYear: semester.academicYear,
              semester: semester.semester,
              reviewPath,
              reviewerYear: data.reviewerYear,
              reviewerFaculty: data.reviewerFaculty,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            }, { merge: true });
          });
          await repairBatch.commit();
        }

        const decision = decideReviewGate(
          Math.min(indexCount + repairCandidates.length, REQUIRED_REVIEW_COUNT),
          REQUIRED_REVIEW_COUNT
        );
        setReviewCount(decision.reviewCount);
        setIsGated(decision.isGated);
        setStatus('ready');
        firestoreCache.set(cacheKey, decision, CACHE_TTL.REVIEW_GATE);
      } catch (error) {
        markUnavailable(error);
      } finally {
        setLoading(false);
      }
  }, [markUnavailable]);

  const retry = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setIsGated(false);
      setStatus('ready');
      setError(null);
      setLoading(false);
      return;
    }

    const semester = getCurrentSemester();
    firestoreCache.invalidate(CACHE_KEYS.reviewGate(user.uid, semester.academicYear, semester.semester));
    await checkGateStatus(user.uid, true);
  }, [checkGateStatus]);

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        void checkGateStatus(user.uid);
      } else {
        setIsGated(false);
        setStatus('ready');
        setError(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [checkGateStatus]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active' && auth.currentUser) {
        void retry();
      }
    });
    return () => subscription.remove();
  }, [retry]);

  // 新規レビュー投稿成功時に呼び出し、Firestore再取得を待たずに件数とゲート状態を即時反映する
  const notifyReviewSubmitted = useCallback(() => {
    if (!auth.currentUser) return;
    if (status !== 'ready') {
      void retry();
      return;
    }

    setReviewCount((prevCount) => {
      const nextCount = prevCount + 1;
      const nextGated = nextCount < REQUIRED_REVIEW_COUNT;
      setIsGated(nextGated);

      const cacheKey = CACHE_KEYS.reviewGate(
        auth.currentUser!.uid,
        currentSemester.academicYear,
        currentSemester.semester
      );
      firestoreCache.set(
        cacheKey,
        { isGated: nextGated, reviewCount: nextCount },
        CACHE_TTL.REVIEW_GATE
      );

      return nextCount;
    });
  }, [currentSemester, retry, status]);

  const setExempt = async () => {
    if (!auth.currentUser) return;

    try {
      const semester = getCurrentSemester();
      const semesterKey = `${semester.academicYear}${semester.semester}`;

      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        reviewExempt: {
          semester: semesterKey,
          grantedAt: serverTimestamp(),
        },
      });

      // キャッシュ無効化
      const cacheKey = CACHE_KEYS.reviewGate(
        auth.currentUser.uid,
        semester.academicYear,
        semester.semester
      );
      firestoreCache.invalidate(cacheKey);

      // 即時反映
      setIsGated(false);
      setStatus('ready');
      setError(null);
    } catch (error) {
      console.error('[useReviewGate] Failed to set exempt:', error);
      throw error;
    }
  };

  return {
    isGated,
    reviewCount,
    requiredCount: REQUIRED_REVIEW_COUNT,
    loading,
    status,
    error,
    retry,
    setExempt,
    notifyReviewSubmitted,
    currentSemester,
  };
};
